import { ILibraryListScope, IScope } from "@/api";
import { useAuth } from "@/contexts/auth/useAuth";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { ProcessingScopesContext } from "./ProcessingScopesContext";

type ProcessingScope = IScope | ILibraryListScope;

interface IProcessingScopesProviderProps {
    children: ReactNode;
}

export const ProcessingScopesProvider = ({
    children,
}: IProcessingScopesProviderProps) => {
    const { authenticated } = useAuth();
    const [storedScopes, setStoredScopes] = useLocalStorage<
        Record<string, ProcessingScope>
    >("processing-scopes", {});

    const [processingScopes, setProcessingScopes] = useState<
        Record<string, ProcessingScope>
    >(storedScopes || {});

    useEffect(() => {
        setStoredScopes(processingScopes);
    }, [processingScopes, setStoredScopes]);

    const addProcessingScope = useCallback((scope: ProcessingScope) => {
        setProcessingScopes((prev) => ({
            ...prev,
            [scope.id]: scope,
        }));
    }, []);

    const removeProcessingScope = useCallback((scopeId: string | number) => {
        setProcessingScopes((prev) => {
            const newScopes = { ...prev };
            delete newScopes[scopeId];
            return newScopes;
        });
    }, []);

    const clearProcessingScopes = useCallback(() => {
        setProcessingScopes({});
    }, []);

    // Clear processing scopes when user logs out (centralized cleanup)
    useEffect(() => {
        if (!authenticated) {
            clearProcessingScopes();
        }
    }, [authenticated, clearProcessingScopes]);

    const value = useMemo(
        () => ({
            processingScopes,
            addProcessingScope,
            removeProcessingScope,
            clearProcessingScopes,
        }),
        [
            processingScopes,
            addProcessingScope,
            removeProcessingScope,
            clearProcessingScopes,
        ]
    );

    return (
        <ProcessingScopesContext.Provider value={value}>
            {children}
        </ProcessingScopesContext.Provider>
    );
};
